"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Asterisk } from "./Asterisk";
import { Logo } from "./Logo";

const footerLinks = [
  { href: "/about", label: "About" },
  { href: "/services", label: "Services" },
  { href: "/thinking", label: "Thinking" },
  { href: "/posts", label: "Posts" },
  { href: "/contact", label: "Contact" },
];

export default function Footer() {
  const pathname = usePathname();
  if (pathname?.startsWith("/admin")) return null;

  return (
    <footer className="bg-warm-deep px-6 pb-9 pt-20 lg:px-10">
      {/* Top row */}
      <div className="mx-auto flex max-w-[1440px] flex-wrap items-end justify-between gap-10 border-b border-[color:var(--rule)] pb-12">
        <div className="text-olive">
          <Logo lockup="full" scale={1.4} color="var(--olive)" />
        </div>
        <p className="m-0 font-serif text-2xl italic text-olive lg:text-[28px]">
          Clarity in complexity.
        </p>
      </div>

      {/* Grid */}
      <div className="mx-auto mt-12 grid max-w-[1440px] gap-10 md:grid-cols-2 lg:grid-cols-[1fr_1fr_1.6fr]">
        <div>
          <p className="mb-3.5 font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-olive">
            Contact
          </p>
          <ul className="m-0 list-none space-y-2 p-0 font-sans text-[15px] text-charcoal/75">
            <li>
              <a
                href="mailto:hello@evokeadvisory.au"
                className="lowercase text-charcoal/75 transition-colors hover:text-olive"
              >
                hello@evokeadvisory.au
              </a>
            </li>
            <li>
              <a
                href="https://www.linkedin.com/in/annacush/"
                target="_blank"
                rel="me noopener noreferrer"
                className="text-charcoal/75 transition-colors hover:text-olive"
              >
                Connect on LinkedIn
              </a>
            </li>
          </ul>
        </div>

        <div>
          <p className="mb-3.5 font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-olive">
            Find
          </p>
          <ul className="m-0 list-none space-y-2 p-0 font-sans text-sm text-charcoal/75">
            {footerLinks.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className="text-charcoal/75 hover:text-olive"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="mb-3.5 font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-olive">
            Acknowledgement
          </p>
          <p className="m-0 max-w-[48ch] font-sans text-sm leading-7 text-charcoal/75">
            Evoke Advisory acknowledges the Traditional Custodians of the lands on which we
            work and live. We pay our respects to Elders past, present, and emerging, and
            recognise their continuing connection to land, waters, and culture.
          </p>
        </div>
      </div>

      {/* Rule + fine print */}
      <div className="mx-auto mt-14 h-px max-w-[1440px] bg-[color:var(--rule)]" />
      <div className="mx-auto mt-6 flex max-w-[1440px] flex-wrap items-center justify-between gap-4 font-sans text-xs text-charcoal/65">
        <span>&copy; 2026 Evoke Advisory. All rights reserved.</span>
        <span className="flex items-center gap-3">
          <Link href="/privacy" className="hover:text-olive">
            Privacy
          </Link>
        </span>
        <span className="inline-flex items-center">
          <Asterisk size={12} color="var(--lime)" pinkCenter />
        </span>
      </div>
    </footer>
  );
}
